import React, { useState, useEffect, useCallback } from 'react';
import BossDialog from "../../components/BossDialog";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Grid, GridColumn, getSelectedState } from "@progress/kendo-react-grid";
import { getter } from "@progress/kendo-react-common";
import { EditPageHeader } from "../../components/EditPageHeader";
import DeleteRoundButton from "./helpercomponents/buttons/DeleteRoundButton";
import { ConfirmDeleteAlert } from "../../ConfirmAlert";
import { useTranslation } from "../../locale/useTranslation";
import { BOOLEAN_STATUS, SUBTITLING_CODE, ENTITYNAME, KEY_ASSET_GROUP_TYPE, SUBTITLING_MODE_TYPE, TOAST_TYPES } from "../constant/constant";
import { utility } from "../utility/utilityProvider";
import * as API from "../API/api";
import { Form, FormElement } from "@progress/kendo-react-form";
import moment from 'moment';
import ToastNotification from '../../components/ToastNotification';

const DATA_ITEM_KEY = "_id";
const SELECTED_FIELD = "selected";
const idGetter = getter(DATA_ITEM_KEY);

/**
 * Unified dialog for selecting / editing prototypes across multiple MAM assets.
 *
 * @param {"ingest"|"transcode"} mode – controls fields, title & submit label.
 * @param {Function}  onClose        – called when the dialog should close.
 * @param {Function}  onSubmit       – receives the full prototype data-item and selected items list.
 * @param {Array}     data           – list of prototypes to pick from.
 * @param {Function}  onChange       – setter for the selected dropdown value.
 * @param {Object}    value          – currently selected prototype in the dropdown.
 * @param {Object}    [initialData]  – optional pre-populated prototype (edit case).
 * @param {Array}     [transcodePrototypesData] – transcode prototypes list (ingest mode only).
 * @param {Object}    [initialTranscodeData]    – pre-populated transcode prototype (ingest mode only).
 * @param {Array}     [selectedItems]           – array of selected MAM assets.
 */
const CreateMultipleMamWorkOrderForm = ({
    mode = "ingest",
    onClose,
    onSubmit,
    data,
    value,
    onChange,
    initialData,
    transcodePrototypesData = [],
    initialTranscodeData = null,
    selectedItems = []
}) => {
    const lang = useTranslation();
    const [transmissionTypes, setTransmissionTypes] = useState([]);
    const [transcodeDefinitions, setTranscodeDefinitions] = useState([]);
    const [dataItem, setDataItem] = useState({});
    const [initialPopulated, setInitialPopulated] = useState(false);

    // Transcode tab state (only used in ingest mode)
    const [transcodeDataItem, setTranscodeDataItem] = useState({});
    const [transcodeValue, setTranscodeValue] = useState({});
    const [transcodeInitialPopulated, setTranscodeInitialPopulated] = useState(false);

    // Tab state (only relevant in ingest mode)
    const [activeTab, setActiveTab] = useState("ingest");

    // Grid and Expected Date controls state
    const [gridItems, setGridItems] = useState([]);
    const [selectedState, setSelectedState] = useState({});
    const [localDataItem, setLocalDataItem] = useState({
        masterExpectedDate: "",
        isStackChecked: false,
        stackOffset: 1
    });

    const onGridChange = (e) => {
        const { name, value, checked, type } = e.target;
        setLocalDataItem((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const isIngest = mode === "ingest";
    const dialogTitle = isIngest ? lang.create_multiple_ingest_wo_label : lang.create_multiple_transcode_wo_label;
    const submitLabel = isIngest ? lang.publish_button : lang.submit_button ;

    const booleans = Object.keys(BOOLEAN_STATUS).map((obj) => ({ _id: BOOLEAN_STATUS[obj], Description: obj }));
    const assetGroups = Object.keys(KEY_ASSET_GROUP_TYPE).map((obj) => ({ _id: KEY_ASSET_GROUP_TYPE[obj], Description: obj }));
    const subtitlingModes = Object.keys(SUBTITLING_MODE_TYPE).map((obj) => ({ _id: SUBTITLING_MODE_TYPE[obj], Description: obj }));

    useEffect(() => {
        loadCombo();
    }, []);

    const loadGridData = (items) => {
        if (items && Array.isArray(items)) {
            const mapped = items.map(item => ({
                _id: item?._id ?? "",
                mamId: item?.AssetId ?? "",
                title: item?.Title ?? item?.mediaEpisode?.Title ?? "",
                episodeNumber: item?.mediaEpisode?.EpisodeNumber ?? "",
                originalItem: item
            }));
            
            const DEFAULT_EPISODE = 999999;
            mapped.sort((a, b) =>
                Number(a.episodeNumber || DEFAULT_EPISODE) -
                Number(b.episodeNumber || DEFAULT_EPISODE)
            );
            setGridItems(mapped);
            setSelectedState({});
        }
    };

    useEffect(() => {
        loadGridData(selectedItems);
    }, [selectedItems]);

    const onHeaderSelectionChange = useCallback((event) => {
        const checkboxElement = event.syntheticEvent.target;
        const checked = checkboxElement.checked;
        const newSelectedState = {};
        event.dataItems.forEach((item) => {
            newSelectedState[idGetter(item)] = checked;
        });
        setSelectedState(newSelectedState);
    }, []);

    const onSelectionChange = useCallback((event) => {
        const newSelectedState = getSelectedState({
            event,
            selectedState: selectedState,
            dataItemKey: DATA_ITEM_KEY,
        });
        setSelectedState(newSelectedState);
    }, [selectedState]);

    const loadCombo = async () => {
        let transmissionTypeRes = await API.getDataLookup(ENTITYNAME.TransmissionTypes, { sort: { SID: 1 } });
        setTransmissionTypes(transmissionTypeRes.data);

        const transcodeDefinitionsRes = await API.getDataLookup(ENTITYNAME.TranscodeDefinition, { sort: { Description: 1 } });
        setTranscodeDefinitions(transcodeDefinitionsRes.data);
    };

    const rawVal = (v) => (v !== null && typeof v === 'object' && v._id !== undefined) ? v._id : v;

    const resolveTransmissionType = (tt, ttList) => {
        if (!tt) return {};
        const lookupId = (typeof tt === 'object' && tt._id !== undefined) ? tt._id : tt;
        const found = ttList.find(t => t._id === lookupId);
        if (found) return found;
        if (typeof tt === 'object' && tt._id !== undefined) {
            return { _id: tt._id, Name: tt.Name };
        }
        return {};
    };

    const buildIngestDataItem = (selectedValue, ttList) => ({
        _id: selectedValue._id,
        SID: selectedValue.SID,
        Description: selectedValue.Description ?? "",
        TransmissionType: resolveTransmissionType(selectedValue.TransmissionType, ttList),
        DirectIngest: { _id: rawVal(selectedValue.DirectIngest), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.DirectIngest)) },
        CensorshipFlag: { _id: rawVal(selectedValue.CensorshipFlag), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.CensorshipFlag)) },
        AssetGroup: { _id: rawVal(selectedValue.AssetGroup), Description: utility.getKeyByValue(KEY_ASSET_GROUP_TYPE, rawVal(selectedValue.AssetGroup)) },
        DualSound: { _id: rawVal(selectedValue.DualSound), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.DualSound)) },
        SubtitlesFlag: { _id: rawVal(selectedValue.SubtitlesFlag), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.SubtitlesFlag)) },
        SubtitlingCode: { _id: rawVal(selectedValue.SubtitlingCode), Description: utility.getKeyByValue(SUBTITLING_CODE, rawVal(selectedValue.SubtitlingCode)) },
        SubtitlingLang: selectedValue.SubtitlingLang ?? "",
        SubtitlingMode: { _id: rawVal(selectedValue.SubtitlingMode), Description: utility.getKeyByValue(SUBTITLING_MODE_TYPE, rawVal(selectedValue.SubtitlingMode)) },
        Thumbnail: { _id: rawVal(selectedValue.Thumbnail), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.Thumbnail)) },
        Language1: selectedValue.Language1 ?? "",
        HD: { _id: rawVal(selectedValue.HD), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.HD)) },
        IsDefault: selectedValue.IsDefault ?? false,
        Language2: selectedValue.Language2 ?? "",
        SecondaryCopy: { _id: rawVal(selectedValue.SecondaryCopy), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.SecondaryCopy)) },
        Archive: selectedValue.Archive ?? false,
    });

    const buildTranscodeDataItem = (selectedValue, ttList) => ({
        _id: selectedValue._id,
        SID: selectedValue.SID,
        Description: selectedValue.Description ?? "",
        TransmissionType: resolveTransmissionType(selectedValue.TransmissionType, ttList),
        TitleFlag: { _id: rawVal(selectedValue.TitleFlag), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.TitleFlag)) },
        LogoFlag: { _id: rawVal(selectedValue.LogoFlag), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.LogoFlag)) },
        LogoOpposite: { _id: rawVal(selectedValue.LogoOpposite), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.LogoOpposite)) },
        EndingFlag: { _id: rawVal(selectedValue.EndingFlag), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.EndingFlag)) },
        Definition: (selectedValue.Definition && Object.keys(selectedValue.Definition).length > 0) ? selectedValue.Definition : {},
        ReverseAudioChannelFlag: { _id: rawVal(selectedValue.ReverseAudioChannelFlag), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.ReverseAudioChannelFlag)) },
        TagFlag: { _id: rawVal(selectedValue.TagFlag), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.TagFlag)) },
        AudioMode: selectedValue.AudioMode ?? "",
        IsDefault: selectedValue.IsDefault ?? false,
        Archive: selectedValue.Archive ?? false,
    });

    const handleFillValues = (overrideValue) => {
        let selectedValue = overrideValue || value;
        if (typeof selectedValue === 'string') {
            selectedValue = data?.find(item => item._id === selectedValue);
        }

        if (selectedValue) {
            if (isIngest) {
                setDataItem(buildIngestDataItem(selectedValue, transmissionTypes));
            } else {
                setDataItem(buildTranscodeDataItem(selectedValue, transmissionTypes));
            }
        } else {
            setDataItem({});
        }
    };

    const handleFillTranscodeValues = (overrideValue) => {
        let selectedValue = overrideValue || transcodeValue;
        if (typeof selectedValue === 'string') {
            selectedValue = transcodePrototypesData?.find(item => item._id === selectedValue);
        }
        if (selectedValue) {
            setTranscodeDataItem(buildTranscodeDataItem(selectedValue, transmissionTypes));
        } else {
            setTranscodeDataItem({});
        }
    };

    useEffect(() => {
        if (!initialPopulated && transmissionTypes.length > 0) {
            if (initialData && Object.keys(initialData).length > 0) {
                if (isIngest) {
                    setDataItem(buildIngestDataItem(initialData, transmissionTypes));
                } else {
                    setDataItem(buildTranscodeDataItem(initialData, transmissionTypes));
                }
                setInitialPopulated(true);
            } else if (data && data.length > 0) {
                setInitialPopulated(true);
            }
        }
    }, [initialData, data, initialPopulated, transmissionTypes, isIngest]);

    useEffect(() => {
        if (isIngest && !transcodeInitialPopulated && transmissionTypes.length > 0) {
            if (initialTranscodeData && Object.keys(initialTranscodeData).length > 0) {
                setTranscodeDataItem(buildTranscodeDataItem(initialTranscodeData, transmissionTypes));
                setTranscodeInitialPopulated(true);
            } else if (transcodePrototypesData && transcodePrototypesData.length > 0) {
                setTranscodeValue(transcodePrototypesData[0]);
                setTranscodeInitialPopulated(true);
            }
        }
    }, [isIngest, initialTranscodeData, transcodePrototypesData, transcodeInitialPopulated, transmissionTypes]);

    const onChangeForm = (e) => {
        if (e.target.name === "Archive") {
            setDataItem({ ...dataItem, Archive: !dataItem.Archive });
        } else if (e.target.name === "IsDefault") {
            setDataItem({ ...dataItem, IsDefault: !dataItem.IsDefault });
        } else {
            setDataItem({ ...dataItem, [e.target.name]: e.target.value });
        }
    };

    const onChangeTranscodeForm = (e) => {
        if (e.target.name === "Archive") {
            setTranscodeDataItem({ ...transcodeDataItem, Archive: !transcodeDataItem.Archive });
        } else if (e.target.name === "IsDefault") {
            setTranscodeDataItem({ ...transcodeDataItem, IsDefault: !transcodeDataItem.IsDefault });
        } else {
            setTranscodeDataItem({ ...transcodeDataItem, [e.target.name]: e.target.value });
        }
    };

    const flattenData = (item) => {
        const flattened = { ...item };
        for (const key of Object.keys(flattened)) {
            const val = flattened[key];
            if (key === 'Definition' || key === 'TransmissionType' || key === 'SubtitlingCode') continue;
            if (key === 'ExpectedDate') {
                flattened[key] = moment.utc(val).valueOf();
            }
            if (val !== null && typeof val === 'object' && !Array.isArray(val) && val._id !== undefined) {
                flattened[key] = val._id;
            }
        }
        return flattened;
    };

    const isFormValid = (item) => {
        if (!item || Object.keys(item).length === 0) return false;
        if (!item.Description || item.Description.trim() === "") return false;
        return true;
    };

    const handleUpdateGridDates = () => {
        const { masterExpectedDate, isStackChecked, stackOffset } = localDataItem;
        if (!masterExpectedDate) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_select_expected_date_error_message });
            return;
        }
        const hasSelection = gridItems.some(item => selectedState[idGetter(item)]);
        if (!hasSelection) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title || "Error", message: lang.please_select_atleast_one_item || "Please select at least one item" });
            return;
        }
        const offsetVal = parseFloat(stackOffset) || 0;
        setGridItems(prevItems => {
            let selectedCount = 0;
            return prevItems.map((item) => {
                if (!selectedState[idGetter(item)]) {
                    return item;
                }
                let dateStr = "";
                if (isStackChecked) {
                    dateStr = moment(masterExpectedDate).add(selectedCount * offsetVal, 'days').format('YYYY-MM-DD');
                } else {
                    dateStr = moment(masterExpectedDate).format('YYYY-MM-DD');
                }
                selectedCount++;
                return {
                    ...item,
                    expectedDate: dateStr
                };
            });
        });
    };

    const handleDeleteItem = (itemToDelete) => {
        setGridItems(prevItems => prevItems.filter(item => item._id !== itemToDelete._id));
        setSelectedState(prevState => {
            const newState = { ...prevState };
            delete newState[idGetter(itemToDelete)];
            return newState;
        });
        ToastNotification({ type: TOAST_TYPES.SUCCESS, title: lang.success_message_title || "Success", message: lang.delete_successfully_grid_data_success_toast_message || "Item deleted successfully" });
    };

    const handleSubmit = () => {
        if (isIngest) {
            if (!isFormValid(dataItem)) {
                ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_attach_ingest_prototype_error_message });
                setActiveTab("ingest");
                return;
            }
            const missingDates = gridItems.filter(i => !i.expectedDate || i.expectedDate.trim() === "");
            if (missingDates.length > 0) {
                ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title , message: lang.please_select_expected_date_for_all_error_message});
                return;
            }
            if (!isFormValid(transcodeDataItem)) {
                ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_attach_transcode_prototype_error_message});
                setActiveTab("transcode");
                return;
            }
            onSubmit({
                ingestPrototype: flattenData(dataItem),
                transcodePrototype: flattenData(transcodeDataItem),
                selectedItems: gridItems.map(item => ({
                    ...item.originalItem,
                    _id: item._id,
                    AssetId: item.mamId,
                    ExpectedDate: item?.expectedDate ? moment.utc(item.expectedDate).valueOf() : null
                }))
            });
        } else {
            if (!isFormValid(dataItem)) {
                ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_attach_transcode_prototype_error_message});
                return;
            }
            onSubmit(flattenData(dataItem));
        }
    };

    const isIngestPrototypeValid = dataItem?.Description?.trim() && dataItem?.AssetGroup?._id && dataItem?.DirectIngest?._id;
    const isTranscodePrototypeValid = transcodeDataItem?.Description?.trim() && transcodeDataItem?.TransmissionType?._id;

    const sectionLabel = { fontSize: "10px", fontWeight: "600", color: "#999", textTransform: "uppercase", letterSpacing: "0.8px", margin: "0" };
    const fieldLabel = { fontSize: "12px", color: "#555", marginBottom: "2px" };

    const tabStyle = (tabName) => ({
        padding: "8px 20px",
        cursor: "pointer",
        fontSize: "13px",
        fontWeight: activeTab === tabName ? "600" : "400",
        color: activeTab === tabName ? "#0d6efd" : "#666",
        borderBottom: activeTab === tabName ? "2px solid #0d6efd" : "2px solid transparent",
        background: "none",
        border: "none",
        borderBottomWidth: "2px",
        borderBottomStyle: "solid",
        borderBottomColor: activeTab === tabName ? "#0d6efd" : "transparent",
        transition: "all 0.2s ease",
    });

    const renderQuickFill = (ddData, ddValue, ddOnChange, fillFn) => (
        <div className="d-flex align-items-center mb-2" style={{ gap: "6px", padding: "6px 10px", backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
            <i className="fas fa-wand-magic-sparkles" style={{ fontSize: "11px", color: "#aaa" }}></i>
            <DropDownList
                data={[...ddData].sort((a, b) => a.Description.localeCompare(b.Description))}
                valueField="_id"
                textField="Description"
                value={ddValue}
                onChange={(e) => ddOnChange(e.value)}
                style={{ flex: 1 }}
                size="small"
            />
            <button
                type="button"
                onClick={() => fillFn()}
                title={lang.fill_fields_from_selected_template_tooltip}
                style={{ background: "none", border: "none", color: "#0d6efd", cursor: "pointer", fontSize: "14px", padding: "2px 6px" }}
            >
                <i className="fas fa-arrow-down"></i>
            </button>
        </div>
    );

    const renderIngestFields = () => (
        <Form
            initialValues={dataItem}
            render={() => (
                <FormElement>
                    <div className="row" style={{ rowGap: "0" }}>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.description_label} <span style={{ color: "red" }}> *</span></label>
                            <input name="Description" type="text" className="form-control form-control-sm" onChange={onChangeForm} value={dataItem.Description || ""} />
                        </div>
                        <div className="col-2 mb-2">
                            <label style={fieldLabel}>{lang.transmission_type_label} </label>
                            <DropDownList style={{ backgroundColor: "white" }} data={transmissionTypes} name="TransmissionType" textField="Name" dataItemKey="_id" value={dataItem.TransmissionType || {}} onChange={onChangeForm} />
                        </div>
                        <div className="col-2 mb-2">
                            <label style={fieldLabel}>{lang.asset_group_label} <span style={{ color: "red" }}> *</span></label>
                            <DropDownList style={{ backgroundColor: "white" }} data={assetGroups} name="AssetGroup" textField="Description" dataItemKey="_id" value={dataItem.AssetGroup || {}} onChange={onChangeForm} />
                        </div>
                        <div className="col-2 mb-2">
                            <label style={fieldLabel}>{lang.language1_label}</label>
                            <input name="Language1" type="text" className="form-control form-control-sm" onChange={onChangeForm} value={dataItem.Language1 || ""} />
                        </div>
                        <div className="col-2 mb-2">
                            <label style={fieldLabel}>{lang.language2_label}</label>
                            <input name="Language2" type="text" className="form-control form-control-sm" onChange={onChangeForm} value={dataItem.Language2 || ""} />
                        </div>
                    </div>
                    <hr style={{ margin: "4px 0 8px", borderColor: "#eee" }} />
                    <p style={sectionLabel}>Flags</p>
                    <div className="row" style={{ rowGap: "0" }}>
                        <div className="col-2 mb-2">
                            <label style={fieldLabel}>{lang.direct_ingest_label} <span style={{ color: "red" }}> *</span></label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="DirectIngest" textField="Description" dataItemKey="_id" value={dataItem.DirectIngest || {}} onChange={onChangeForm} />
                        </div>
                        <div className="col-2 mb-2">
                            <label style={fieldLabel}>{lang.censorship_flag_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="CensorshipFlag" textField="Description" dataItemKey="_id" value={dataItem.CensorshipFlag || {}} onChange={onChangeForm} />
                        </div>
                        <div className="col-2 mb-2">
                            <label style={fieldLabel}>{lang.dual_sound_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="DualSound" textField="Description" dataItemKey="_id" value={dataItem.DualSound || {}} onChange={onChangeForm} />
                        </div>
                        <div className="col-2 mb-2">
                            <label style={fieldLabel}>{lang.thumbnail_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="Thumbnail" textField="Description" dataItemKey="_id" value={dataItem.Thumbnail || {}} onChange={onChangeForm} />
                        </div>
                        <div className="col-2 mb-2">
                            <label style={fieldLabel}>{lang.hd_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="HD" textField="Description" dataItemKey="_id" value={dataItem.HD || {}} onChange={onChangeForm} />
                        </div>
                         <div className="col-2 mb-2">
                            <label style={fieldLabel}>{lang.secondary_copy_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="SecondaryCopy" textField="Description" dataItemKey="_id" value={dataItem.SecondaryCopy || {}} onChange={onChangeForm} />
                        </div>
                    </div>
                    <hr style={{ margin: "4px 0 8px", borderColor: "#eee" }} />
                    <p style={sectionLabel}>{lang.subtitling_label}</p>
                    <div className="row" style={{ rowGap: "0" }}>
                        <div className="col-3 mb-2">
                            <label style={fieldLabel}>{lang.subtitles_flag_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="SubtitlesFlag" textField="Description" dataItemKey="_id" value={dataItem.SubtitlesFlag || {}} onChange={onChangeForm} />
                        </div>
                        <div className="col-3 mb-2">
                            <label style={fieldLabel}>{lang.subtitling_code_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={Object.keys(SUBTITLING_CODE).map((obj) => ({ _id: SUBTITLING_CODE[obj], Description: obj }))} name="SubtitlingCode" textField="Description" dataItemKey="_id" value={dataItem.SubtitlingCode || {}} onChange={onChangeForm} />
                        </div>
                        <div className="col-3 mb-2">
                            <label style={fieldLabel}>{lang.subtitling_lang_label}</label>
                            <input name="SubtitlingLang" type="text" className="form-control form-control-sm" onChange={onChangeForm} value={dataItem.SubtitlingLang || ""} />
                        </div>
                        <div className="col-3 mb-2">
                            <label style={fieldLabel}>{lang.subtitling_mode_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={subtitlingModes} name="SubtitlingMode" textField="Description" dataItemKey="_id" value={dataItem.SubtitlingMode || {}} onChange={onChangeForm} />
                        </div>
                    </div>
                    <p style={sectionLabel}>{lang.definition_label}</p>
                    <div className="row align-items-center" style={{ rowGap: "0" }}>
                        <div className="col-6 mb-2">
                            <label style={fieldLabel}>{lang.definition_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={transcodeDefinitions} name="Definition" textField="Description" dataItemKey="_id" value={dataItem.Definition || {}} onChange={onChangeForm} />
                        </div>
                        <div className="col-6 mb-2 d-flex align-items-center" style={{ gap: "20px", paddingTop: "18px" }}>
                            <label style={{ fontSize: "12px", color: "#555", cursor: "pointer", margin: 0 }}>
                                <input type="checkbox" name="IsDefault" onChange={onChangeForm} checked={dataItem.IsDefault || false} style={{ marginRight: "5px" }} />
                                {lang.set_as_default_label}
                            </label>
                            <label style={{ fontSize: "12px", color: "#555", cursor: "pointer", margin: 0 }}>
                                <input type="checkbox" name="Archive" onChange={onChangeForm} checked={dataItem.Archive || false} style={{ marginRight: "5px" }} />
                                {lang.archive}
                            </label>
                        </div>
                    </div>
                </FormElement>
            )}
        />
    );

    const renderTranscodeFields = (currentDataItem, currentOnChangeForm) => (
        <Form
            initialValues={currentDataItem}
            render={() => (
                <FormElement>
                    <div className="row" style={{ rowGap: "0" }}>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.description_label} <span style={{ color: "red" }}> *</span></label>
                            <input name="Description" type="text" className="form-control form-control-sm" onChange={currentOnChangeForm} value={currentDataItem.Description || ""} />
                        </div>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.transmission_type_label} <span style={{ color: "red" }}> *</span></label>
                            <DropDownList style={{ backgroundColor: "white" }} data={transmissionTypes} name="TransmissionType" textField="Name" dataItemKey="_id" value={currentDataItem.TransmissionType || {}} onChange={currentOnChangeForm} />
                        </div>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.audio_mode_label}</label>
                            <input name="AudioMode" type="text" className="form-control form-control-sm" onChange={currentOnChangeForm} value={currentDataItem.AudioMode || ""} />
                        </div>
                    </div>
                    <hr style={{ margin: "4px 0 8px", borderColor: "#eee" }} />
                    <p style={sectionLabel}>{lang.flags_label}</p>
                    <div className="row" style={{ rowGap: "0" }}>
                        <div className="col-3 mb-2">
                            <label style={fieldLabel}>{lang.title_flag_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="TitleFlag" textField="Description" dataItemKey="_id" value={currentDataItem.TitleFlag || {}} onChange={currentOnChangeForm} />
                        </div>
                        <div className="col-3 mb-2">
                            <label style={fieldLabel}>{lang.logo_flag_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="LogoFlag" textField="Description" dataItemKey="_id" value={currentDataItem.LogoFlag || {}} onChange={currentOnChangeForm} />
                        </div>
                        <div className="col-3 mb-2">
                            <label style={fieldLabel}>{lang.logo_opposite_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="LogoOpposite" textField="Description" dataItemKey="_id" value={currentDataItem.LogoOpposite || {}} onChange={currentOnChangeForm} />
                        </div>
                        <div className="col-3 mb-2">
                            <label style={fieldLabel}>{lang.ending_flag_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="EndingFlag" textField="Description" dataItemKey="_id" value={currentDataItem.EndingFlag || {}} onChange={currentOnChangeForm} />
                        </div>
                        <div className="col-3 mb-2">
                            <label style={fieldLabel}>{lang.reverse_audio_channel_flag_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="ReverseAudioChannelFlag" textField="Description" dataItemKey="_id" value={currentDataItem.ReverseAudioChannelFlag || {}} onChange={currentOnChangeForm} />
                        </div>
                        <div className="col-3 mb-2">
                            <label style={fieldLabel}>{lang.tag_flag_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="TagFlag" textField="Description" dataItemKey="_id" value={currentDataItem.TagFlag || {}} onChange={currentOnChangeForm} />
                        </div>
                    </div>
                    <hr style={{ margin: "4px 0 8px", borderColor: "#eee" }} />
                    <p style={sectionLabel}>{lang.definition_label}</p>
                    <div className="row align-items-center" style={{ rowGap: "0" }}>
                        <div className="col-6 mb-2">
                            <label style={fieldLabel}>{lang.definition_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={transcodeDefinitions} name="Definition" textField="Description" dataItemKey="_id" value={currentDataItem.Definition || {}} onChange={currentOnChangeForm} />
                        </div>
                        <div className="col-6 mb-2 d-flex align-items-center" style={{ gap: "20px", paddingTop: "18px" }}>
                            <label style={{ fontSize: "12px", color: "#555", cursor: "pointer", margin: 0 }}>
                                <input type="checkbox" name="IsDefault" onChange={currentOnChangeForm} checked={currentDataItem.IsDefault || false} style={{ marginRight: "5px" }} />
                                {lang.set_as_default_label}
                            </label>
                            <label style={{ fontSize: "12px", color: "#555", cursor: "pointer", margin: 0 }}>
                                <input type="checkbox" name="Archive" onChange={currentOnChangeForm} checked={currentDataItem.Archive || false} style={{ marginRight: "5px" }} />
                                {lang.archive}
                            </label>
                        </div>
                    </div>
                </FormElement>
            )}
        />
    );

    const renderAssetsGrid = () => {
        const ExpectedDateCell = (props) => {
            const { dataItem } = props;
            const isRowSelected = selectedState[idGetter(dataItem)];
            return (
                <td style={{ verticalAlign: "middle" }}>
                    <input
                        type="date"
                        className="form-control form-control-sm"
                        disabled={!isRowSelected}
                        value={dataItem.expectedDate || ""}
                        onChange={(e) => {
                            const val = e.target.value;
                            setGridItems(prev => prev.map(row => row._id === dataItem._id ? { ...row, expectedDate: val } : row));
                        }}
                    />
                </td>
            );
        };

        const DeleteCell = (props) => {
            const { dataItem } = props;
            return (
                <td style={{ verticalAlign: "middle", textAlign: "center" }}>
                    <DeleteRoundButton
                        title={lang.delete_button_tooltip}
                        onClick={() => ConfirmDeleteAlert(() => handleDeleteItem(dataItem), () => { })}
                    />
                </td>
            );
        };

        return (
            <div className="mt-2 border-top pt-2 d-flex flex-column" style={{ borderTopColor: "#eee", flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
                <div className="d-flex align-items-center flex-wrap mb-2 p-2 border rounded flex-shrink-0" style={{ backgroundColor: "#f8f9fa", gap: "14px" }}>
                    <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                        <label style={{ fontSize: "12px", color: "#555", margin: 0, whiteSpace: "nowrap", fontWeight: "500" }}>{lang.expected_date_label || "Expected Date Picker"}</label>
                        <input
                            name="masterExpectedDate"
                            type="date"
                            className="form-control form-control-sm"
                            style={{ width: "150px" }}
                            value={localDataItem.masterExpectedDate || ""}
                            onChange={onGridChange}
                        />
                    </div>
                    <label className="d-flex align-items-center mb-0" style={{ fontSize: "12px", color: "#555", cursor: "pointer", gap: "5px", margin: 0 }}>
                        <span style={{ fontWeight: "500" }}>Stack</span>
                        <input
                            name="isStackChecked"
                            type="checkbox"
                            checked={localDataItem.isStackChecked}
                            onChange={onGridChange}
                            style={{ cursor: "pointer" }}
                        />
                    </label>
                    <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                        <span style={{ fontSize: "12px", color: "#555", whiteSpace: "nowrap", fontWeight: "500" }}>Stack Offset</span>
                        <input
                            name="stackOffset"
                            type="number"
                            className="form-control form-control-sm"
                            style={{ width: "70px" }}
                            disabled={!localDataItem.isStackChecked}
                            value={localDataItem.stackOffset ?? 1}
                            onChange={onGridChange}
                        />
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        style={{ fontSize: "12px", padding: "3px 14px", fontWeight: "500" }}
                        onClick={handleUpdateGridDates}
                    >
                        {lang.update_button || "Update"}
                    </button>
                </div>

                <div className="border rounded" style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", backgroundColor: "#fff" }}>
                    <Grid
                        style={{ height: "100%" }}
                        data={gridItems.map((item) => ({
                            ...item,
                            [SELECTED_FIELD]: selectedState[idGetter(item)] || false,
                        }))}
                        dataItemKey={DATA_ITEM_KEY}
                        selectedField={SELECTED_FIELD}
                        selectable={{
                            enabled: true,
                            drag: false,
                            cell: false,
                            mode: "multiple",
                        }}
                        resizable={true}
                        onSelectionChange={onSelectionChange}
                        onHeaderSelectionChange={onHeaderSelectionChange}
                    >
                        <GridColumn
                            field={SELECTED_FIELD}
                            width="50px"
                            headerSelectionValue={
                                gridItems.length > 0 &&
                                gridItems.findIndex((item) => !selectedState[idGetter(item)]) === -1
                            }
                        />
                        <GridColumn cell={DeleteCell} width="60px" locked={true} />
                        <GridColumn field="mamId" title={lang.mam_id_column} width="160px" />
                        <GridColumn field="title" title={lang.title_label} />
                        <GridColumn field="episodeNumber" title={lang.episode_number_label} width="140px" />
                        <GridColumn field="expectedDate" title={lang.expected_date_label} width="180px" cell={ExpectedDateCell} />
                    </Grid>
                </div>
            </div>
        );
    };

    return (
        <BossDialog onClose={onClose} title={dialogTitle} width={"1050px"} height={"95vh"}>
            <style>{`
                .boss-dialog-content {
                    overflow: hidden !important;
                    display: flex !important;
                    flex-direction: column !important;
                    flex: 1 !important;
                    min-height: 0 !important;
                    padding: 12px 20px !important;
                }
            `}</style>
            <div style={{ display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
                <div style={{ flexShrink: 0 }}>
                    <div className="row align-items-center mb-2">
                        <div className="col">
                            <EditPageHeader
                                saveButtonTitle={submitLabel}
                                showTitle={false}
                                onCancel={onClose}
                                onSubmit={handleSubmit}
                            />
                        </div>
                    </div>

                    {/* ── Tabs (only in ingest mode) ── */}
                    {isIngest && (
                        <div style={{ display: "flex", borderBottom: "1px solid #dee2e6", marginBottom: "10px" }}>
                            <button type="button" style={tabStyle("ingest")} onClick={() => setActiveTab("ingest")}>
                                <i className="fas fa-indent" style={{ marginRight: "6px" }}></i>{lang.ingest_label}
                                {isIngestPrototypeValid && (
                                    <i className="fas fa-check-circle" style={{ marginLeft: "6px", color: "#198754", fontSize: "11px" }}></i>
                                )}
                            </button>
                            <button type="button" style={tabStyle("transcode")} onClick={() => setActiveTab("transcode")}>
                                <i className="fas fa-globe" style={{ marginRight: "6px" }}></i>{lang.transcode_label}
                                {isTranscodePrototypeValid && (
                                    <i className="fas fa-check-circle" style={{ marginLeft: "6px", color: "#198754", fontSize: "11px" }}></i>
                                )}
                            </button>
                        </div>
                    )}

                    {/* ── INGEST MODE ── */}
                    {isIngest && activeTab === "ingest" && (
                        <>
                            {renderQuickFill(data, value, onChange, handleFillValues)}
                            {renderIngestFields()}
                        </>
                    )}

                    {isIngest && activeTab === "transcode" && (
                        <>
                            {renderQuickFill(transcodePrototypesData, transcodeValue, setTranscodeValue, handleFillTranscodeValues)}
                            {renderTranscodeFields(transcodeDataItem, onChangeTranscodeForm)}
                        </>
                    )}

                    {/* ── TRANSCODE MODE (no tabs) ── */}
                    {!isIngest && (
                        <>
                            {renderQuickFill(data, value, onChange, handleFillValues)}
                            {renderTranscodeFields(dataItem, onChangeForm)}
                        </>
                    )}
                </div>

                {/* ── Expected Date & Selected MAM Assets Grid below three sections ── */}
                <div style={{ flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    {renderAssetsGrid()}
                </div>
            </div>
        </BossDialog>
    );
};

export default CreateMultipleMamWorkOrderForm;
